import * as THREE from "three";
import { Context } from "../core/context";
import {
  DBEntity,
  DbLine,
  DbMesh,
  SceneManager,
  ViewManager,
} from "@/CadEngine";
import {
  DrawOperationMovePointBeforeParams,
  DrawOperationMovePointParams,
  IModifyOperation,
  ModifyOperateState,
} from "./IModifyOperation";

import {
  CommonColor,
  DimensionsAnnotationModifyReturnData,
} from "@/core/Parameters";
import { TextModel } from "@/core/TextModel";

class DimensionsAnnotationModify implements IModifyOperation {
  private m_context: Context;
  private curPoint: THREE.Vector3 = new THREE.Vector3();
  private m_locationObject: THREE.Line;
  private m_auxLineGroupCurrent: THREE.Group = new THREE.Group();
  private m_currentAuxLine: THREE.LineSegments | undefined;
  private m_currentAuxEdgeLine1: THREE.LineSegments | undefined;
  private m_currentAuxEdgeLine2: THREE.LineSegments | undefined;
  private m_returnData: DimensionsAnnotationModifyReturnData[] = [];
  private m_points: THREE.Vector3[] = [];
  private m_textModel: TextModel;
  private m_curText: THREE.Mesh | undefined;
  private m_textObjs: THREE.Mesh[] = [];
  private m_curDistance: string = "";
  private m_TempLinePoints: THREE.Vector3[] = [];
  constructor(context: Context) {
    this.m_context = context;
    this.m_locationObject = context.ToolsManager.CreatedLocationLine();
    this.m_context.SceneManager.AuxModel.add(this.m_auxLineGroupCurrent);
    this.m_textModel = new TextModel(this.m_context);
  }

  OnMovePointBefore(params: DrawOperationMovePointBeforeParams): void {}
  OnMovePoint(point: DrawOperationMovePointParams): void {
    this.curPoint = point.point;
    this.m_locationObject.position.copy(this.curPoint);
    this.updateTempObject();
  }
  OnDownPoint(point: DrawOperationMovePointParams): void {
    if (!this.m_points.length) {
      this.createTempLine();
    }

    if (this.m_points.length == 2) {
      this.m_points.length = 0;
      this.m_curText = undefined;
      this.m_returnData.push({
        distance: this.m_curDistance,
        linePoints: [...this.m_TempLinePoints],
      });
      this.m_TempLinePoints.length = 0;
      this.m_curDistance = "";
      return;
    }
    if (this.m_points.length < 2) {
      if (this.m_points.length == 1) {
        this.m_curDistance = (this.m_points[0].distanceTo(point.point) * 1000)
          .toFixed(2)
          .toString();
        this.m_curText = this.m_textModel.CreateText({
          text: this.m_curDistance,
        });
        this.m_context.SceneManager.AuxModel.add(this.m_curText);
        let textMatrix = this.getTextMatrix(
          this.m_points[0].clone(),
          point.point.clone()
        );
        this.m_curText.applyMatrix4(textMatrix);
        this.m_curText.visible = false;
        this.m_textObjs.push(this.m_curText);
      }
      this.m_points.push(point.point);
    }
  }
  onKeyDown(e: KeyboardEvent): void {
    e.preventDefault();
    if (e.code == "Escape") {
      if (this.m_points.length) {
        this.m_points.length = 0;
        this.m_TempLinePoints.length = 0;
        this.m_curDistance = "";
        let deleteObjs: any[] = [
          this.m_currentAuxLine!,
          this.m_currentAuxLine!,
          this.m_currentAuxEdgeLine1!,
          this.m_currentAuxEdgeLine2!,
        ];
        if (this.m_curText) {
          deleteObjs.push(this.m_curText);
        }
        this.m_context.ToolsManager.ObjectsClear(deleteObjs);
        this.m_curText = undefined;
      }
      this.m_context.ViewManager.Render();
    }
  }
  onKeyUp(e: KeyboardEvent): void {}

  OnZoom(camera: THREE.Camera): void {}

  Finish(operate: ModifyOperateState) {
    this.clear();
    if (operate == ModifyOperateState.complete) {
      return this.m_returnData;
    } else if (operate == ModifyOperateState.cancel) {
      return [];
    }
  }
  OnWheel(e: WheelEvent): void {}

  onMouseMove(e?: PointerEvent): void {}

  onMouseDown(e: PointerEvent): void {
    if (e.button !== 0) return;
  }

  onMouseUp(e: PointerEvent): void {
    if (e.button !== 0) return;
  }

  private createTempLine() {
    let geo = new THREE.BufferGeometry();
    let mat = new THREE.LineBasicMaterial();
    this.m_currentAuxLine = new THREE.LineSegments(geo, mat);
    this.m_currentAuxEdgeLine1 = new THREE.LineSegments(geo.clone(), mat);
    this.m_currentAuxEdgeLine2 = new THREE.LineSegments(geo.clone(), mat);
    this.m_currentAuxLine.frustumCulled = false;
    this.m_currentAuxEdgeLine1.frustumCulled = false;
    this.m_currentAuxEdgeLine2.frustumCulled = false;

    this.m_auxLineGroupCurrent.add(
      this.m_currentAuxLine,
      this.m_currentAuxEdgeLine1,
      this.m_currentAuxEdgeLine2
    );
  }

  private updateTempObject() {
    if (this.m_points.length) {
      let zAxis = new THREE.Vector3(0, 0, 1);
      let auxStartPoint = this.m_points[0];
      let auxEndPoint = this.curPoint;
      let vector1 = new THREE.Vector3();
      let vector2 = new THREE.Vector3();

      if (this.m_points.length == 2) {
        auxEndPoint = this.m_points[1];
        let line3 = new THREE.Line3(auxStartPoint, auxEndPoint);
        let closestLine3ToCurrentPoint = new THREE.Vector3();
        line3.closestPointToPoint(
          this.curPoint,
          false,
          closestLine3ToCurrentPoint
        );

        let closestLine3ToCurrentPointDistance =
          closestLine3ToCurrentPoint.distanceTo(this.curPoint);
        vector1 = auxStartPoint.clone().sub(auxEndPoint).normalize();
        vector2 = this.curPoint.clone().sub(auxEndPoint).normalize();
        let cross = vector2.clone().cross(vector1).z;

        if (cross > 0) {
          auxStartPoint = auxStartPoint.clone().add(
            vector1
              .clone()
              .negate()
              .applyAxisAngle(zAxis, Math.PI / 2)
              .setLength(closestLine3ToCurrentPointDistance)
          );
          auxEndPoint = auxEndPoint.clone().add(
            vector1
              .clone()
              .applyAxisAngle(zAxis, -Math.PI / 2)
              .setLength(closestLine3ToCurrentPointDistance)
          );
        } else {
          auxStartPoint = auxStartPoint.clone().add(
            vector1
              .clone()
              .negate()
              .applyAxisAngle(zAxis, -Math.PI / 2)
              .setLength(closestLine3ToCurrentPointDistance)
          );
          auxEndPoint = auxEndPoint.clone().add(
            vector1
              .clone()
              .applyAxisAngle(zAxis, Math.PI / 2)
              .setLength(closestLine3ToCurrentPointDistance)
          );
        }
        this.m_currentAuxEdgeLine1?.geometry.setFromPoints([
          this.m_points[0],
          auxStartPoint,
        ]);
        this.m_currentAuxEdgeLine2?.geometry.setFromPoints([
          this.m_points[1],
          auxEndPoint,
        ]);
      }
      this.m_currentAuxLine?.geometry.setFromPoints([
        auxStartPoint,
        auxEndPoint,
      ]);
      if (this.m_curText) {
        this.m_curText.visible = true;
        let dirV = new THREE.Vector3()
          .subVectors(this.m_points[0], auxStartPoint)
          .normalize()
          .negate();
        let cP = new THREE.Vector3()
          .lerpVectors(auxStartPoint, auxEndPoint, 0.5)
          .add(dirV.setLength(0.25));
        this.m_curText.position.copy(cP);
      }
      this.m_TempLinePoints = [
        this.m_points[0],
        auxStartPoint,
        auxEndPoint,
        this.m_points[1],
      ];
    }
  }

  private clear() {
    this.m_context.ToolsManager.ObjectsClear([
      this.m_locationObject,
      this.m_currentAuxLine!,
      this.m_currentAuxEdgeLine1!,
      this.m_currentAuxEdgeLine2!,
      ...this.m_textObjs,
    ]);
    this.m_context.ToolsManager.GroupClear(this.m_auxLineGroupCurrent);
  }
  private getTextMatrix(startP: THREE.Vector3, endP: THREE.Vector3) {
    let XAxis = new THREE.Vector3(1, 0, 0);
    let zAxis = new THREE.Vector3(0, 0, 1);
    let CenterP = startP.clone().add(endP).multiplyScalar(0.5);
    let v = endP.clone().sub(startP).normalize();
    let angle = v.angleTo(XAxis);
    if (v.clone().cross(XAxis).z > 0) {
      angle = -angle;
    }
    if (Math.abs(angle) > Math.PI / 2) {
      angle = angle - Math.PI;
    }
    let q = new THREE.Quaternion().setFromAxisAngle(zAxis, angle);
    let s = new THREE.Vector3(1, 1, 1);
    return new THREE.Matrix4().compose(CenterP, q, s);
  }
}

export { DimensionsAnnotationModify };
